import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Wachtwoord moet minstens 8 tekens zijn");
      return;
    }
    if (password !== confirm) {
      toast.error("De wachtwoorden komen niet overeen");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      await refresh?.();
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset mislukt — vraag een nieuwe link aan");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1 h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-0 focus-visible:border-slate-900";

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2 bg-white antialiased"
      data-testid="reset-password-page"
      style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}
    >
      <div className="relative flex flex-col px-6 py-8 lg:px-12 lg:py-10 bg-white">
        <div
          className="text-slate-900 text-base tracking-tight"
          style={{ fontFamily: 'Figtree, Inter, ui-sans-serif, system-ui, sans-serif', fontWeight: 500 }}
        >
          The Brand Hub
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-sm">
            <div className="flex flex-col items-center mb-6">
              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center mb-4">
                {done ? <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={1.75} /> : <KeyRound className="w-5 h-5 text-slate-500" strokeWidth={1.75} />}
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight text-center">
                {done ? "Wachtwoord bijgewerkt" : "Nieuw wachtwoord"}
              </h1>
              <p className="text-sm text-slate-500 mt-1.5 text-center">
                {done ? "Je bent ingelogd. Even geduld — we brengen je naar het dashboard." : "Kies een nieuw wachtwoord van minstens 8 tekens."}
              </p>
            </div>

            {!token ? (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                Deze link is onvolledig. Vraag een nieuwe aan via <Link to="/forgot-password" className="underline">Wachtwoord vergeten</Link>.
              </div>
            ) : !done && (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Nieuw wachtwoord</Label>
                  <PasswordInput
                    testid="reset-password-input"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minstens 8 tekens"
                    className={inputCls}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Bevestig wachtwoord</Label>
                  <PasswordInput
                    testid="reset-password-confirm"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Herhaal het wachtwoord"
                    className={inputCls}
                  />
                </div>
                <Button
                  data-testid="reset-submit"
                  disabled={busy}
                  type="submit"
                  className="w-full h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800 shadow-none text-sm font-medium disabled:opacity-60"
                >
                  {busy ? "Opslaan…" : "Wachtwoord opslaan"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:block" style={{ backgroundColor: "#b8cff8" }} aria-hidden="true" />
    </div>
  );
}
